// A person's arcade progress, rebuilt from the event log (pure; replay is the source of truth).
// Events carry what was decided on the device at the time (play-day, XP, plays allowed), so
// the projection needs no pack and gives the same answer on every phone.
import { CardsCompacted } from '../../../shared/compact'
import { levelFor } from './level'
import { review, type CardState, type Result } from './srs'
import type { CardSetId } from './types'

export interface ArcadeEvent {
  id: string
  type: string
  at: number
  personId: string
  payload: unknown
}

export interface SessionStarted {
  machine: CardSetId
  sessionId: string
  day: string
  /** Plays left in the day's pool when this capsule started (for the record; not read back). */
  allowed: number
}
export interface CardAnswered {
  machine: CardSetId
  sessionId: string
  cardId: string
  result: Result
  xp: number
  day: string
}
export interface SessionCompleted {
  machine: CardSetId
  sessionId: string
  day: string
  right: number
  total: number
  bonusXp: number
  /** Sound-effect card shown on the reward. */
  sfx?: string
}
export interface PlaysGranted {
  /** Person receiving the plays (the organiser sends them). */
  for: string
  /** Older grants named a machine; plays are one pool now, so it's ignored. */
  machine?: CardSetId
  n: number
  day: string
}

export interface Progress {
  cards: Record<string, CardState>
  xp: number
  level: number
  sessions: number
  stickers: number
  /** `${day}|${machine}` → capsules started (each machine's cards vary by play number). Plays left are domain/arcade/plays. */
  used: Record<string, number>
  sfxSeen: string[]
  playDays: string[]
  /** Last bonus that hasn't been seen on screen yet (for the "Bonus from Dad!" note). */
  lastBonusAt: number
}

export const emptyProgress = (): Progress => ({
  cards: {},
  xp: 0,
  level: 1,
  sessions: 0,
  stickers: 0,
  used: {},
  sfxSeen: [],
  playDays: [],
  lastBonusAt: 0,
})

export const playKey = (day: string, machine: CardSetId) => `${day}|${machine}`

/** Folds events (any order) into progress for `personId`. */
export function project(events: readonly ArcadeEvent[], personId: string): Progress {
  const p = emptyProgress()
  const sfx = new Set<string>()
  const days = new Set<string>()
  const sorted = [...events].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))
  // Answer XP is kept apart: a 12-month snapshot (B12) resets it to what the folded answers earned.
  let answeredXp = 0
  let otherXp = 0
  for (const e of sorted) {
    if (e.type === 'plays.granted') {
      const g = e.payload as PlaysGranted
      if (g.for === personId) p.lastBonusAt = e.at
      continue
    }
    if (e.personId !== personId) continue
    switch (e.type) {
      case 'session.started': {
        const s = e.payload as SessionStarted
        const k = playKey(s.day, s.machine)
        p.used[k] = (p.used[k] ?? 0) + 1
        break
      }
      case 'card.answered': {
        const a = e.payload as CardAnswered
        p.cards[a.cardId] = review(p.cards[a.cardId], a.result, a.day)
        answeredXp += a.xp
        break
      }
      case 'cards.compacted': {
        // Authoritative at its cutoff (it sorts just before it): the cards and answer XP up to then.
        const c = CardsCompacted.safeParse(e.payload)
        if (!c.success) break
        Object.assign(p.cards, c.data.cards)
        answeredXp = c.data.answeredXp
        break
      }
      case 'session.completed': {
        const c = e.payload as SessionCompleted
        otherXp += c.bonusXp
        p.sessions++
        p.stickers++
        days.add(c.day)
        if (c.sfx) sfx.add(c.sfx)
        break
      }
      case 'learn.completed':
        // A Learn session keeps the streak going too (no sticker: those come from capsules).
        days.add((e.payload as { day: string }).day)
        break
      case 'sfx.triggered':
        sfx.add((e.payload as { cardId: string }).cardId)
        break
    }
  }
  p.xp = answeredXp + otherXp
  p.level = levelFor(p.xp)
  p.sfxSeen = [...sfx]
  p.playDays = [...days].sort()
  return p
}

const dayBefore = (d: string) => {
  const prev = new Date(`${d}T00:00:00Z`)
  prev.setUTCDate(prev.getUTCDate() - 1)
  return prev.toISOString().slice(0, 10)
}

/**
 * Consecutive play-days with a finished capsule or Learn session, ending today (or yesterday).
 * `restDays` (travel days: a flight, a long train) don't break it when missed, and don't count.
 */
export function streak(p: Progress, today: string, yesterday: string, restDays: ReadonlySet<string> = new Set()): number {
  const days = new Set(p.playDays)
  const skip = (d: string) => {
    while (!days.has(d) && restDays.has(d)) d = dayBefore(d)
    return d
  }
  let d: string | null = days.has(today) ? today : skip(yesterday)
  if (!days.has(d)) return 0
  let n = 0
  while (d && days.has(d)) {
    n++
    d = skip(dayBefore(d))
  }
  return n
}

/** Travel days (a leg leaves that day, in the trip's time zone): they don't break a streak. */
export function travelDays(legs: readonly { departsAt: string }[], dateOf: (iso: string) => string): Set<string> {
  return new Set(legs.map((l) => dateOf(l.departsAt)))
}
